import { isNil } from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableChildrenFn,
  Droppable,
  OnDragEndResponder,
} from 'react-beautiful-dnd';
import { Easing, TouchableOpacity } from 'react-native';
import { View } from '@shared/components/View';
import { MBAnimation } from '@shared/components/animation';
import { MBIconV2 } from '@shared/components/image';
import { MBTextBody1, MBTextBody1Bold } from '@shared/components/typography';
import { RenameSheet } from '@shared/scenes/workflows/subforms/RenameSheet';
import { ColorThemeContext, getAnchorName, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { useWorkflowDispatch, useWorkflowTemplateState } from '../../context/context';
import { WorkspaceDetail } from '../../hooks/useWorkspaceDetails';
import { BuilderComponentOpenContext } from '../context';
import { MBWorkflowAddComponent } from './addComponent';
import { MBWorkflowComponentEditor } from './componentEditor';

interface Props {
  workflowTemplateId: string;
  loading: boolean;
  isSubform: boolean;
  workspaceDetails: Map<string, WorkspaceDetail>;
  createMode?: boolean;
}

const ADD_COMPONENT_ID = 'add-component';

export function MBWorkflowEditComponent({
  workflowTemplateId,
  loading,
  isSubform,
  workspaceDetails,
}: Props) {
  const colors = useContext(ColorThemeContext);
  const { componentEditorOpen, setComponentEditorOpen } = useContext(BuilderComponentOpenContext);
  const [showRenameForm, setShowRenameForm] = useState(false);
  const anchorName = getAnchorName('workflow-edit-rename', workflowTemplateId);
  const { workflowTemplate } = useWorkflowTemplateState();
  const dispatch = useWorkflowDispatch();

  const components = workflowTemplate?.components.filter(component =>
    isNil(component.managedByComponentId),
  );
  const name = workflowTemplate?.name;

  useEffect(() => {
    if (!components) {
      return;
    }
    const allIds = [ADD_COMPONENT_ID, ...components.map(({ id }) => id)];
    if (isNil(componentEditorOpen) || !allIds.includes(componentEditorOpen)) {
      setComponentEditorOpen(undefined);
    }
  }, [components, componentEditorOpen, setComponentEditorOpen]);

  const handleSetComponentEditorOpen = useCallback(
    (whichComponent: string | undefined) => {
      if (!isNil(whichComponent)) {
        setComponentEditorOpen(whichComponent);
      } else {
        // Required to not make the close click open a new sheet
        setTimeout(() => {
          setComponentEditorOpen(whichComponent);
        }, 150);
      }
    },
    [setComponentEditorOpen],
  );

  const disableOpenSheets = typeof componentEditorOpen === 'string' || loading;

  const onDragEnd = useCallback<OnDragEndResponder>(
    result => {
      if (!result.destination) {
        return;
      }

      dispatch({
        type: 'reorderComponent',
        args: {
          source: result.source.index,
          target: result.destination.index,
        },
      });
    },
    [dispatch],
  );

  const onChangeName = useCallback(
    (newName: string) => {
      dispatch({ type: 'setName', args: newName });
    },
    [dispatch],
  );

  const renderDraggableComponentEditor = useCallback<DraggableChildrenFn>(
    (provided, snapshot, rubric) => {
      const component = components[rubric.source.index];
      const style = {
        ...provided.draggableProps.style,
        backgroundColor: colors.background,
        ...(snapshot.isDragging && {
          boxShadow: '2px 2px 10px rgba(0,0,0,0.2)',
          borderRadius: grid(1),
          opacity: snapshot.isDragging ? 0.5 : 1,
        }),
      };
      return (
        <div
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={style}
          ref={provided.innerRef}
        >
          <MBWorkflowComponentEditor
            key={component.id}
            workflowTemplateId={workflowTemplateId}
            components={components}
            component={component}
            disableOpenSheet={disableOpenSheets}
            setShowingSheet={opened =>
              handleSetComponentEditorOpen(opened ? component.id : undefined)
            }
            showingSheet={componentEditorOpen === component.id}
            workspaceDetails={workspaceDetails}
          />
        </div>
      );
    },
    [
      components,
      colors.background,
      workflowTemplateId,
      disableOpenSheets,
      componentEditorOpen,
      workspaceDetails,
      handleSetComponentEditorOpen,
    ],
  );
  return (
    <View fill>
      <View row center padding={1} style={{ backgroundColor: colors.highlightActive }}>
        <View row center style={{ width: '70%' }}>
          <MBIconV2 name="info-outline" size={grid(3)} color={colors.foregroundActive} />
          <View paddingLeft={1} />
          <MBTextBody1 numberOfLines={2}>
            {i18n('workflowTemplate.customizingFieldsMessage')}
            <RenameButton
              name={name}
              setShowRenameForm={setShowRenameForm}
              showRenameForm={showRenameForm}
              anchorName={anchorName}
            />
            {showRenameForm && (
              <RenameSheet
                anchorName={anchorName}
                workflowName={name}
                setWorkflowName={onChangeName}
                isSubform={isSubform}
                onClose={() => setShowRenameForm(false)}
              />
            )}
          </MBTextBody1>
        </View>
      </View>
      <View paddingHorizontal={8}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="components" renderClone={renderDraggableComponentEditor}>
            {provided => (
              <div ref={provided.innerRef}>
                {components?.map((component, i) => (
                  <View key={component.id}>
                    <Draggable draggableId={component.id} index={i}>
                      {renderDraggableComponentEditor}
                    </Draggable>
                  </View>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <View paddingLeft={4}>
          <MBWorkflowAddComponent
            isSubform={isSubform}
            showingSheet={componentEditorOpen === ADD_COMPONENT_ID}
            setShowingSheet={opened =>
              handleSetComponentEditorOpen(opened ? ADD_COMPONENT_ID : undefined)
            }
            disableOpenSheet={disableOpenSheets}
          />
        </View>
      </View>
    </View>
  );
}

function RenameButton({
  name,
  setShowRenameForm,
  showRenameForm,
  anchorName,
}: {
  name: string;
  setShowRenameForm: (show: boolean) => void;
  showRenameForm: boolean;
  anchorName: string;
}) {
  const colors = useContext(ColorThemeContext);
  return (
    <TouchableOpacity
      onPress={() => setShowRenameForm(true)}
      style={{ flexDirection: 'row', alignItems: 'center' }}
    >
      <View style={{ anchorName, maxWidth: 380 }}>
        <MBTextBody1Bold numberOfLines={1}>{name}</MBTextBody1Bold>
      </View>
      <View marginLeft={0.5}>
        <MBAnimation
          duration={200}
          easing={Easing.inOut(Easing.quad)}
          disabled={!showRenameForm}
          rotate={{ from: '0deg', to: '-180deg' }}
        >
          <MBIconV2
            color={colors.foregroundActive}
            name="arrow-point-down-heavy"
            size={grid(1.75)}
          />
        </MBAnimation>
      </View>
    </TouchableOpacity>
  );
}
